import React, { useState, useCallback, useEffect, ChangeEvent, useRef } from 'react';
import { useStyletron } from 'baseui';
import { LabelMedium } from 'baseui/typography';
import { useSnackbar } from 'baseui/snackbar';
import { FlexGrid, FlexGridItem } from 'baseui/flex-grid';
import { TableBuilder, TableBuilderColumn } from 'baseui/table-semantic';
import { Pagination } from 'baseui/pagination';
import { Button, KIND, SIZE } from 'baseui/button';
import { Select } from 'baseui/select';
import { Input } from 'baseui/input';
import { useService } from './useService';
import ScheduleDialog from './ScheduleDialog';
import { EBonusMoneyLambdaReqStatus, COL_NAMES, formatDate, FILTERABLE_COLUMNS, SORTABLE_COLUMNS } from './helper';

// Define column config - simpler than the DataList approach
const PAGE_SIZE = 20;

// Define the interface for the row items
interface BonusMoneyLambdaItem {
	id?: string;
	eventName: string;
	status: string;
	cashbackDenomInRs: number;
	bonusDenomInRs: number;
	totalUserCount: number;
	totalBonusAmountInRs: number;
	totalCashbackAmountInRs: number;
	bonusExpiryTime?: number;
	cashbackExpiryTime?: number;
	approvedBy?: string;
	approvedAt?: number;
	scheduledBy?: string;
	scheduledAt?: number;
	createdAt?: number;
	updatedAt?: number;
	cronExpression?: string;
	fileName?: string;
	s3Path?: string;
	[key: string]: any;
}

// Define filter options based on FILTERABLE_COLUMNS
const FILTER_OPTIONS = {
	// Only create filter options for columns defined in FILTERABLE_COLUMNS
	...(FILTERABLE_COLUMNS.includes(COL_NAMES.status) && {
		[COL_NAMES.status]: [
			{ label: 'Pending', id: EBonusMoneyLambdaReqStatus.PENDING },
			{ label: 'Approved', id: EBonusMoneyLambdaReqStatus.APPROVED },
			{ label: 'Rejected', id: EBonusMoneyLambdaReqStatus.REJECTED },
			{ label: 'Scheduled', id: EBonusMoneyLambdaReqStatus.SCHEDULED },
			{ label: 'Processing', id: EBonusMoneyLambdaReqStatus.PROCESSING },
			{ label: 'Uploaded', id: EBonusMoneyLambdaReqStatus.UPLOADED },
			{ label: 'Failed', id: EBonusMoneyLambdaReqStatus.FAILED }
		]
	})
};

// Add a utility function to check if a column is filterable
const isColumnFilterable = (columnId: string) => FILTERABLE_COLUMNS.includes(columnId);

const BonusMoneyLambda: React.FC = () => {
	const [css] = useStyletron();
	const { enqueue } = useSnackbar();

	// State
	const [isScheduleOpen, setIsScheduleOpen] = useState(false);
	const [selectedRequest, setSelectedRequest] = useState<any>(null);
	const [currentPage, setCurrentPage] = useState(1);
	const [sortColumn, setSortColumn] = useState(COL_NAMES.createdAt);
	const [sortDirection, setSortDirection] = useState<'ASC' | 'DESC'>('DESC');
	const [statusFilter, setStatusFilter] = useState([{ label: 'Pending', id: EBonusMoneyLambdaReqStatus.PENDING }]);
	const [searchQuery, setSearchQuery] = useState('');
	const [approvedByFilter, setApprovedByFilter] = useState('');
	const [scheduledByFilter, setScheduledByFilter] = useState('');
	const [createdAtFilter, setCreatedAtFilter] = useState<Date | null>(null);
	const [updatedAtFilter, setUpdatedAtFilter] = useState<Date | null>(null);
	const [data, setData] = useState<BonusMoneyLambdaItem[]>([]);
	const [totalCount, setTotalCount] = useState(0);
	const [activeFilters, setActiveFilters] = useState<Map<string, any>>(
		new Map([[COL_NAMES.status, EBonusMoneyLambdaReqStatus.PENDING]])
	);

	// Track if initial data load has been completed
	const initialDataLoadRef = useRef(false);

	// Use service hook for API calls - use constant page size
	const { updateLambdaStatus, fetchData } = useService();

	// Load data with client-side filtering, sorting, and pagination
	const loadData = useCallback(async () => {
		try {
			// Fetch data only once or when forced to refresh
			const shouldFetchData = !initialDataLoadRef.current;
			const params = shouldFetchData ? {} : { forceRefresh: false };

			const result = await fetchData(params);

			if (result && result.data && Array.isArray(result.data)) {
				// Mark that we've loaded initial data
				initialDataLoadRef.current = true;

				let processedData = [...result.data] as BonusMoneyLambdaItem[];

				// Apply filters client-side
				if (activeFilters.size > 0) {
					activeFilters.forEach((value, columnKey) => {
						if (FILTERABLE_COLUMNS.includes(columnKey)) {
							processedData = processedData.filter((item) => {
								return item[columnKey] === value;
							});
						}
					});
				}

				// Apply search query client-side for event name
				if (searchQuery && isColumnFilterable(COL_NAMES.eventName)) {
					processedData = processedData.filter((item) => item.eventName.toLowerCase().includes(searchQuery.toLowerCase()));
				}

				// Apply approvedBy filter
				if (approvedByFilter && isColumnFilterable(COL_NAMES.approvedBy)) {
					processedData = processedData.filter((item) =>
						item.approvedBy?.toLowerCase().includes(approvedByFilter.toLowerCase())
					);
				}

				// Apply scheduledBy filter
				if (scheduledByFilter && isColumnFilterable(COL_NAMES.scheduledBy)) {
					processedData = processedData.filter((item) =>
						item.scheduledBy?.toLowerCase().includes(scheduledByFilter.toLowerCase())
					);
				}

				// Apply createdAt filter
				if (createdAtFilter && isColumnFilterable(COL_NAMES.createdAt)) {
					const filterDate = new Date(createdAtFilter);
					filterDate.setHours(0, 0, 0, 0); // Start of day
					const nextDay = new Date(filterDate);
					nextDay.setDate(nextDay.getDate() + 1); // End of day

					processedData = processedData.filter((item) => {
						if (!item.createdAt) return false;
						const itemDate = new Date(item.createdAt);
						return itemDate >= filterDate && itemDate < nextDay;
					});
				}

				// Apply updatedAt filter
				if (updatedAtFilter && isColumnFilterable(COL_NAMES.updatedAt)) {
					const filterDate = new Date(updatedAtFilter);
					filterDate.setHours(0, 0, 0, 0); // Start of day
					const nextDay = new Date(filterDate);
					nextDay.setDate(nextDay.getDate() + 1); // End of day

					processedData = processedData.filter((item) => {
						if (!item.updatedAt) return false;
						const itemDate = new Date(item.updatedAt);
						return itemDate >= filterDate && itemDate < nextDay;
					});
				}

				// Apply sorting client-side
				if (sortColumn && SORTABLE_COLUMNS.includes(sortColumn)) {
					processedData.sort((a, b) => {
						// Handle numbers
						if (typeof a[sortColumn] === 'number' && typeof b[sortColumn] === 'number') {
							return sortDirection === 'ASC' ? a[sortColumn] - b[sortColumn] : b[sortColumn] - a[sortColumn];
						}

						// Handle strings
						const aValue = String(a[sortColumn] || '').toLowerCase();
						const bValue = String(b[sortColumn] || '').toLowerCase();

						return sortDirection === 'ASC' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
					});
				}

				// Store the total count for pagination
				const filteredCount = processedData.length;

				// Apply pagination client-side
				const startIndex = (currentPage - 1) * PAGE_SIZE;
				const paginatedData = processedData.slice(startIndex, startIndex + PAGE_SIZE);

				// Update the UI with filtered, sorted, and paginated data
				setData(paginatedData);
				setTotalCount(filteredCount);
			}
		} catch (error) {
			// Silent error handling
		}
	}, [
		fetchData,
		sortColumn,
		sortDirection,
		activeFilters,
		searchQuery,
		approvedByFilter,
		scheduledByFilter,
		createdAtFilter,
		updatedAtFilter,
		currentPage
	]);

	// Load data only on initial mount or when dependencies change
	useEffect(() => {
		loadData();
		// Cleanup function to ensure we reset the flag when component unmounts
		return () => {
			initialDataLoadRef.current = false;
		};
	}, [loadData]);

	// Handle approve action with explicit refresh
	const handleApprove = async (request: BonusMoneyLambdaItem) => {
		const success = await updateLambdaStatus(request.eventName, EBonusMoneyLambdaReqStatus.APPROVED);
		if (success) {
			enqueue({
				message: 'Request approved successfully',
				overrides: {
					Root: {
						style: {
							backgroundColor: '#27AE60',
							borderRadius: '8px',
							boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
						}
					},
					Message: {
						style: {
							color: 'white',
							fontWeight: 500
						}
					}
				}
			});
			// Refresh data with force flag
			initialDataLoadRef.current = false;
			loadData();
		} else {
			enqueue({ message: 'Failed to approve request' });
		}
	};

	const handleReject = async (request: BonusMoneyLambdaItem) => {
		const success = await updateLambdaStatus(request.eventName, EBonusMoneyLambdaReqStatus.REJECTED);
		if (success) {
			enqueue({
				message: 'Request rejected successfully',
				overrides: {
					Root: {
						style: {
							backgroundColor: '#27AE60',
							borderRadius: '8px',
							boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
						}
					},
					Message: {
						style: {
							color: 'white',
							fontWeight: 500
						}
					}
				}
			});
			// Refresh data with force flag
			initialDataLoadRef.current = false;
			loadData();
		} else {
			enqueue({ message: 'Failed to reject request' });
		}
	};

	const handleSchedule = (request: BonusMoneyLambdaItem) => {
		setSelectedRequest(request);
		setIsScheduleOpen(true);
	};

	const handleScheduleSubmit = async (timestamp: number) => {
		if (!selectedRequest) return;

		const success = await updateLambdaStatus(selectedRequest.eventName, EBonusMoneyLambdaReqStatus.SCHEDULED, timestamp);

		if (success) {
			enqueue({
				message: 'Request scheduled successfully',
				overrides: {
					Root: {
						style: {
							backgroundColor: '#27AE60',
							borderRadius: '8px',
							boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
						}
					},
					Message: {
						style: {
							color: 'white',
							fontWeight: 500
						}
					}
				}
			});
			setIsScheduleOpen(false);
			// Refresh data with force flag
			initialDataLoadRef.current = false;
			loadData();
		} else {
			enqueue({ message: 'Failed to schedule request' });
		}
	};

	// Handle pagination - Just update the page, no API call needed
	const handlePageChange = (nextPage: number) => {
		setCurrentPage(nextPage);
	};

	// Handle sorting - only allow sorting on columns defined in SORTABLE_COLUMNS
	const handleSort = (column: string) => {
		if (!SORTABLE_COLUMNS.includes(column)) return;

		if (sortColumn === column) {
			// Toggle direction
			setSortDirection(sortDirection === 'ASC' ? 'DESC' : 'ASC');
		} else {
			// New column, set as default descending
			setSortColumn(column);
			setSortDirection('DESC');
		}
	};

	// Handle filter changes
	const handleStatusFilterChange = (params: any) => {
		// Only apply filter if status is filterable
		if (!isColumnFilterable(COL_NAMES.status)) return;

		const newFilters = new Map(activeFilters);

		if (params.value.length === 0) {
			// If cleared, remove the filter
			newFilters.delete(COL_NAMES.status);
		} else {
			// Set the new filter value
			newFilters.set(COL_NAMES.status, params.value[0].id);
		}

		setStatusFilter(params.value);
		setActiveFilters(newFilters);
		setCurrentPage(1); // Reset to first page
	};

	// Handle search - used for filtering by event name
	const handleSearchChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		// Only apply search if eventName is filterable
		if (isColumnFilterable(COL_NAMES.eventName)) {
			setSearchQuery(e.target.value);
			setCurrentPage(1); // Reset to first page
		}
	};

	// Handle approvedBy filter change
	const handleApprovedByChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		if (isColumnFilterable(COL_NAMES.approvedBy)) {
			setApprovedByFilter(e.target.value);
			setCurrentPage(1); // Reset to first page
		}
	};

	// Handle scheduledBy filter change
	const handleScheduledByChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		if (isColumnFilterable(COL_NAMES.scheduledBy)) {
			setScheduledByFilter(e.target.value);
			setCurrentPage(1); // Reset to first page
		}
	};

	// Handle createdAt filter change
	const handleCreatedAtChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		if (isColumnFilterable(COL_NAMES.createdAt)) {
			setCreatedAtFilter(e.target.value ? new Date(e.target.value) : null);
			setCurrentPage(1); // Reset to first page
		}
	};

	// Handle updatedAt filter change
	const handleUpdatedAtChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		if (isColumnFilterable(COL_NAMES.updatedAt)) {
			setUpdatedAtFilter(e.target.value ? new Date(e.target.value) : null);
			setCurrentPage(1); // Reset to first page
		}
	};

	// Check if a column is sortable
	const isColumnSortable = (columnId: string) => SORTABLE_COLUMNS.includes(columnId);

	// Render filters section based on filterable columns
	const renderFilters = () => {
		return (
			<div>
				<div className={css({ display: 'flex', marginBottom: '16px', gap: '16px', flexWrap: 'wrap' })}>
					{/* Status filter */}
					{isColumnFilterable(COL_NAMES.status) && (
						<div className={css({ width: '250px' })}>
							<div className={css({ marginBottom: '4px', fontSize: '12px', fontWeight: '500' })}>Filter by Status</div>
							<Select
								options={FILTER_OPTIONS[COL_NAMES.status]}
								value={statusFilter}
								placeholder='Filter by Status'
								onChange={handleStatusFilterChange}
								clearable
							/>
						</div>
					)}

					{/* Event name search */}
					{isColumnFilterable(COL_NAMES.eventName) && (
						<div className={css({ width: '250px' })}>
							<div className={css({ marginBottom: '4px', fontSize: '12px', fontWeight: '500' })}>
								Search on Event Name
							</div>
							<Input
								value={searchQuery}
								onChange={handleSearchChange}
								placeholder='Search on Event Name'
								clearable
								clearOnEscape
							/>
						</div>
					)}

					{/* Approved By filter */}
					{isColumnFilterable(COL_NAMES.approvedBy) && (
						<div className={css({ width: '250px' })}>
							<div className={css({ marginBottom: '4px', fontSize: '12px', fontWeight: '500' })}>
								Filter on Approved By
							</div>
							<Input
								value={approvedByFilter}
								onChange={handleApprovedByChange}
								placeholder='Filter on Approved By'
								clearable
								clearOnEscape
							/>
						</div>
					)}

					{/* Scheduled By filter */}
					{isColumnFilterable(COL_NAMES.scheduledBy) && (
						<div className={css({ width: '250px' })}>
							<div className={css({ marginBottom: '4px', fontSize: '12px', fontWeight: '500' })}>
								Filter on Scheduled By
							</div>
							<Input
								value={scheduledByFilter}
								onChange={handleScheduledByChange}
								placeholder='Filter on Scheduled By'
								clearable
								clearOnEscape
							/>
						</div>
					)}
				</div>

				<div className={css({ display: 'flex', marginBottom: '16px', gap: '16px', flexWrap: 'wrap' })}>
					{/* Created At filter */}
					{isColumnFilterable(COL_NAMES.createdAt) && (
						<div className={css({ width: '250px' })}>
							<div className={css({ marginBottom: '4px', fontSize: '12px', fontWeight: '500' })}>
								Filter by Created At Date (IST)
							</div>
							<Input type='date' onChange={handleCreatedAtChange} clearable clearOnEscape placeholder='' />
						</div>
					)}

					{/* Updated At filter */}
					{isColumnFilterable(COL_NAMES.updatedAt) && (
						<div className={css({ width: '250px' })}>
							<div className={css({ marginBottom: '4px', fontSize: '12px', fontWeight: '500' })}>
								Filter by Updated At Date (IST)
							</div>
							<Input type='date' onChange={handleUpdatedAtChange} clearable clearOnEscape placeholder='' />
						</div>
					)}
				</div>
			</div>
		);
	};

	return (
		<div>
			<div className={css({ padding: '20px' })}>
				<FlexGrid flexGridColumnCount={1} marginBottom='20px'>
					<FlexGridItem>
						<LabelMedium>Bonus Money Lambda Requests</LabelMedium>
					</FlexGridItem>
				</FlexGrid>

				{/* Filters */}
				{renderFilters()}

				{/* Table */}
				<div className={css({ marginBottom: '20px' })}>
					<TableBuilder
						data={data}
						sortColumn={sortColumn}
						sortOrder={sortDirection}
						onSort={(column) => handleSort(column)}>
						{/* 1. Index */}
						<TableBuilderColumn header='Index' id='index' sortable={false}>
							{(_row: BonusMoneyLambdaItem, rowIndex?: number) =>
								String((currentPage - 1) * PAGE_SIZE + (rowIndex || 0) + 1)
							}
						</TableBuilderColumn>

						{/* 2. ROW ACTIONS */}
						<TableBuilderColumn header='Actions' id='actions' sortable={false}>
							{(row: BonusMoneyLambdaItem) => (
								<div className={css({ display: 'flex', gap: '8px' })}>
									{row.status === EBonusMoneyLambdaReqStatus.PENDING && (
										<>
											<Button onClick={() => handleApprove(row)} kind={KIND.primary} size={SIZE.compact}>
												Approve
											</Button>
											<Button onClick={() => handleReject(row)} kind={KIND.secondary} size={SIZE.compact}>
												Reject
											</Button>
										</>
									)}

									{row.status === EBonusMoneyLambdaReqStatus.APPROVED && (
										<Button
											onClick={() => handleSchedule(row)}
											kind={KIND.primary}
											size={SIZE.compact}
											overrides={{
												BaseButton: {
													style: {
														backgroundColor: '#27AE60',
														':hover': {
															backgroundColor: '#219653'
														}
													}
												}
											}}>
											Schedule
										</Button>
									)}
								</div>
							)}
						</TableBuilderColumn>

						{/* 3. Created At (IST) */}
						<TableBuilderColumn
							header='Created At (IST)'
							id={COL_NAMES.createdAt}
							sortable={isColumnSortable(COL_NAMES.createdAt)}
							overrides={{
								TableHeadCell: {
									style: {
										minWidth: '180px'
									}
								},
								TableBodyCell: {
									style: {
										minWidth: '180px'
									}
								}
							}}>
							{(row: BonusMoneyLambdaItem) => (row[COL_NAMES.createdAt] ? formatDate(row[COL_NAMES.createdAt]) : '')}
						</TableBuilderColumn>

						{/* 4. Event Name */}
						<TableBuilderColumn
							header='Event Name'
							id={COL_NAMES.eventName}
							sortable={isColumnSortable(COL_NAMES.eventName)}
							overrides={{
								TableHeadCell: {
									style: {
										minWidth: '240px'
									}
								},
								TableBodyCell: {
									style: {
										minWidth: '240px'
									}
								}
							}}>
							{(row: BonusMoneyLambdaItem) => row[COL_NAMES.eventName] || ''}
						</TableBuilderColumn>

						{/* 5. Total User Count */}
						<TableBuilderColumn
							header='Total User Count'
							id={COL_NAMES.totalUserCount}
							numeric
							sortable={isColumnSortable(COL_NAMES.totalUserCount)}>
							{(row: BonusMoneyLambdaItem) => row[COL_NAMES.totalUserCount] || 0}
						</TableBuilderColumn>

						{/* 6. Cashback per user (Rs) */}
						<TableBuilderColumn
							header='Cashback per user (Rs)'
							id={COL_NAMES.cashbackDenomInRs}
							numeric
							sortable={isColumnSortable(COL_NAMES.cashbackDenomInRs)}>
							{(row: BonusMoneyLambdaItem) => row[COL_NAMES.cashbackDenomInRs] || 0}
						</TableBuilderColumn>

						{/* 7. Total Cashback Amount In (Rs) */}
						<TableBuilderColumn
							header='Total Cashback Amount In (Rs)'
							id={COL_NAMES.totalCashbackAmountInRs}
							numeric
							sortable={isColumnSortable(COL_NAMES.totalCashbackAmountInRs)}>
							{(row: BonusMoneyLambdaItem) => row[COL_NAMES.totalCashbackAmountInRs] || 0}
						</TableBuilderColumn>

						{/* 8. Cashback Expiry Time (IST) */}
						<TableBuilderColumn
							header='Cashback Expiry Time (IST)'
							id={COL_NAMES.cashbackExpiryTime}
							sortable={isColumnSortable(COL_NAMES.cashbackExpiryTime)}
							overrides={{
								TableHeadCell: {
									style: {
										minWidth: '180px'
									}
								},
								TableBodyCell: {
									style: {
										minWidth: '180px'
									}
								}
							}}>
							{(row: BonusMoneyLambdaItem) =>
								row[COL_NAMES.cashbackExpiryTime] ? formatDate(row[COL_NAMES.cashbackExpiryTime]) : ''
							}
						</TableBuilderColumn>

						{/* 9. Bonus per user (Rs) */}
						<TableBuilderColumn
							header='Bonus per user (Rs)'
							id={COL_NAMES.bonusDenomInRs}
							numeric
							sortable={isColumnSortable(COL_NAMES.bonusDenomInRs)}>
							{(row: BonusMoneyLambdaItem) => row[COL_NAMES.bonusDenomInRs] || 0}
						</TableBuilderColumn>

						{/* 10. Total Bonus Amount In (Rs) */}
						<TableBuilderColumn
							header='Total Bonus Amount In(Rs)'
							id={COL_NAMES.totalBonusAmountInRs}
							numeric
							sortable={isColumnSortable(COL_NAMES.totalBonusAmountInRs)}>
							{(row: BonusMoneyLambdaItem) => row[COL_NAMES.totalBonusAmountInRs] || 0}
						</TableBuilderColumn>

						{/* 11. Bonus Expiry Time (IST) */}
						<TableBuilderColumn
							header='Bonus Expiry Time (IST)'
							id={COL_NAMES.bonusExpiryTime}
							sortable={isColumnSortable(COL_NAMES.bonusExpiryTime)}
							overrides={{
								TableHeadCell: {
									style: {
										minWidth: '180px'
									}
								},
								TableBodyCell: {
									style: {
										minWidth: '180px'
									}
								}
							}}>
							{(row: BonusMoneyLambdaItem) =>
								row[COL_NAMES.bonusExpiryTime] ? formatDate(row[COL_NAMES.bonusExpiryTime]) : ''
							}
						</TableBuilderColumn>

						{/* 12. Approved By */}
						<TableBuilderColumn
							header='Approved By'
							id={COL_NAMES.approvedBy}
							sortable={isColumnSortable(COL_NAMES.approvedBy)}>
							{(row: BonusMoneyLambdaItem) => row[COL_NAMES.approvedBy] || ''}
						</TableBuilderColumn>

						{/* 13. Approved At (IST) */}
						<TableBuilderColumn
							header='Approved At (IST)'
							id={COL_NAMES.approvedAt}
							sortable={isColumnSortable(COL_NAMES.approvedAt)}
							overrides={{
								TableHeadCell: {
									style: {
										minWidth: '180px'
									}
								},
								TableBodyCell: {
									style: {
										minWidth: '180px'
									}
								}
							}}>
							{(row: BonusMoneyLambdaItem) => (row[COL_NAMES.approvedAt] ? formatDate(row[COL_NAMES.approvedAt]) : '')}
						</TableBuilderColumn>

						{/* 14. Scheduled By */}
						<TableBuilderColumn
							header='Scheduled By'
							id={COL_NAMES.scheduledBy}
							sortable={isColumnSortable(COL_NAMES.scheduledBy)}>
							{(row: BonusMoneyLambdaItem) => row[COL_NAMES.scheduledBy] || ''}
						</TableBuilderColumn>

						{/* 15. Scheduled At (IST) */}
						<TableBuilderColumn
							header='Scheduled At (IST)'
							id={COL_NAMES.scheduledAt}
							sortable={isColumnSortable(COL_NAMES.scheduledAt)}
							overrides={{
								TableHeadCell: {
									style: {
										minWidth: '180px'
									}
								},
								TableBodyCell: {
									style: {
										minWidth: '180px'
									}
								}
							}}>
							{(row: BonusMoneyLambdaItem) =>
								row[COL_NAMES.scheduledAt] ? formatDate(row[COL_NAMES.scheduledAt]) : ''
							}
						</TableBuilderColumn>

						{/* 16. Updated At (IST) */}
						<TableBuilderColumn
							header='Updated At (IST)'
							id={COL_NAMES.updatedAt}
							sortable={isColumnSortable(COL_NAMES.updatedAt)}
							overrides={{
								TableHeadCell: {
									style: {
										minWidth: '180px'
									}
								},
								TableBodyCell: {
									style: {
										minWidth: '180px'
									}
								}
							}}>
							{(row: BonusMoneyLambdaItem) => (row[COL_NAMES.updatedAt] ? formatDate(row[COL_NAMES.updatedAt]) : '')}
						</TableBuilderColumn>

						{/* 17. Cron Expression */}
						<TableBuilderColumn header='Cron Expression' id={COL_NAMES.cronExpression}>
							{(row: BonusMoneyLambdaItem) => row[COL_NAMES.cronExpression] || ''}
						</TableBuilderColumn>

						{/* 18. File Name */}
						<TableBuilderColumn header='File Name' id={COL_NAMES.fileName}>
							{(row: BonusMoneyLambdaItem) => row[COL_NAMES.fileName] || ''}
						</TableBuilderColumn>

						{/* 19. S3 Path */}
						<TableBuilderColumn header='S3 Path' id={COL_NAMES.s3Path}>
							{(row: BonusMoneyLambdaItem) => row[COL_NAMES.s3Path] || ''}
						</TableBuilderColumn>

						{/* 20. Req Status */}
						<TableBuilderColumn header='Req Status' id={COL_NAMES.status} sortable={isColumnSortable(COL_NAMES.status)}>
							{(row: BonusMoneyLambdaItem) => row[COL_NAMES.status] || ''}
						</TableBuilderColumn>
					</TableBuilder>
				</div>

				{/* Pagination */}
				{totalCount > PAGE_SIZE && (
					<Pagination
						currentPage={currentPage}
						numPages={Math.ceil(totalCount / PAGE_SIZE)}
						onPageChange={({ nextPage }) => handlePageChange(nextPage)}
					/>
				)}
			</div>

			{isScheduleOpen && (
				<ScheduleDialog isOpen={isScheduleOpen} onClose={() => setIsScheduleOpen(false)} onSubmit={handleScheduleSubmit} />
			)}
		</div>
	);
};

export default BonusMoneyLambda;
